import { CARD_H, CARD_W, GROUP_GAP, ROW, SP, TITLE_H, ends } from "@/lib/family";

const push = (m, k, v) => m.set(k, [...(m.get(k) || []), v]);
const avg = (xs) => xs.reduce((s, v) => s + v, 0) / xs.length;

// Rows: BFS for a first guess, then push every child below all of its parents
function generations(comp, edges) {
  const adj = new Map();
  edges.forEach((e) => {
    const [a, b] = ends(e), d = e.kind === "pc" ? 1 : 0;
    push(adj, a, { to: b, d }); push(adj, b, { to: a, d: -d });
  });
  const gen = new Map([[comp[0], 0]]), q = [comp[0]];
  while (q.length) {
    const id = q.shift();
    for (const { to, d } of adj.get(id) || []) if (!gen.has(to)) { gen.set(to, gen.get(id) + d); q.push(to); }
  }
  const pc = edges.filter((e) => e.kind === "pc");
  for (let i = 0, moved = true; moved && i < comp.length; i++) {
    moved = false;
    pc.forEach((e) => { if (gen.get(e.down) <= gen.get(e.up)) { gen.set(e.down, gen.get(e.up) + 1); moved = true; } });
  }
  const min = Math.min(...gen.values());
  const rows = [];
  comp.forEach((id) => (rows[gen.get(id) - min] ||= []).push(id));
  return rows.filter(Boolean);
}

// Orders nodes so linked ones sit next to each other, starting each run from an end of the chain
function chain(nodes, linksOf, rank) {
  const sorted = [...nodes].sort((a, b) => rank(a) - rank(b));
  const deg = (n) => linksOf(n).filter((m) => nodes.includes(m)).length;
  const order = [];
  while (order.length < nodes.length) {
    const rest = sorted.filter((n) => !order.includes(n));
    let cur = rest.filter((n) => deg(n) > 0).sort((a, b) => deg(a) - deg(b))[0] || rest[0];
    while (cur) {
      order.push(cur);
      cur = linksOf(cur).filter((m) => nodes.includes(m) && !order.includes(m)).sort((a, b) => deg(a) - deg(b))[0];
    }
  }
  return order;
}

function layoutComp(comp, edges) {
  const rows = generations(comp, edges);
  const ups = new Map(), hybridUps = new Map(), nb = new Map(), partners = new Map();
  edges.forEach((e) => {
    const [a, b] = ends(e);
    push(nb, a, b); push(nb, b, a);
    if (e.kind === "pc") push(ups, e.down, e.up);
    if (e.type === "hybrid") push(hybridUps, e.down, e.up);
  });
  const rowOf = new Map(rows.flatMap((r, i) => r.map((id) => [id, i])));
  hybridUps.forEach((ps) => ps.forEach((a) => ps.forEach((b) => { if (a !== b && rowOf.get(a) === rowOf.get(b)) push(partners, a, b); })));

  const x = new Map();
  const desired = (id) => {
    const ps = (ups.get(id) || []).filter((p) => x.has(p));
    const ns = ps.length ? ps : (nb.get(id) || []).filter((p) => x.has(p));
    return ns.length ? avg(ns.map((p) => x.get(p))) : null;
  };

  rows.forEach((row) => {
    const placed = [];
    const fits = (lo, hi) => placed.every((p) => hi + SP <= p.lo + 0.5 || lo >= p.hi + SP - 0.5);
    const put = (ids, lo) => { ids.forEach((id, i) => x.set(id, lo + i * SP)); placed.push({ lo, hi: lo + (ids.length - 1) * SP }); };
    // 1. Hybrids are pinned to the exact midpoint of their parents
    const pinned = new Set();
    row.forEach((id) => {
      const ps = (hybridUps.get(id) || []).filter((p) => x.has(p));
      if (ps.length < 2) return;
      const d = avg(ps.map((p) => x.get(p)));
      if (fits(d, d)) { put([id], d); pinned.add(id); }
    });
    // 2. Everyone else moves in blocks: siblings of one parent stay together, and blocks joined by
    //    hybrid co-parents sit next to each other with those co-parents on their facing edges
    const free = row.filter((id) => !pinned.has(id));
    const rank = (id) => desired(id) ?? Infinity;
    const gOf = new Map(free.map((id) => {
      const up = (ups.get(id) || [])[0];
      return [id, up && !hybridUps.get(id)?.length ? `p:${up}` : `n:${id}`];
    }));
    const groups = new Map();
    free.forEach((id) => push(groups, gOf.get(id), id));
    const linked = (id) => (partners.get(id) || []).filter((p) => gOf.has(p));
    const gLinks = (k) => [...new Set(groups.get(k).flatMap(linked).map((p) => gOf.get(p)).filter((g) => g !== k))];
    const gRank = (k) => avg(groups.get(k).map(rank).map((d) => (d === Infinity ? 1e9 : d)));
    const par = new Map([...groups.keys()].map((k) => [k, k]));
    const find = (k) => (par.get(k) === k ? k : find(par.get(k)));
    groups.forEach((_, k) => gLinks(k).forEach((g) => par.set(find(k), find(g))));
    const supers = new Map();
    groups.forEach((_, k) => push(supers, find(k), k));
    const units = [...supers.values()].map((keys) => {
      const gs = chain(keys, gLinks, gRank);
      const ids = gs.flatMap((k, i) => {
        const touches = (g) => (id) => g && linked(id).some((p) => gOf.get(p) === g);
        const members = groups.get(k);
        const left = members.filter(touches(gs[i - 1]));
        const right = members.filter((id) => !left.includes(id) && touches(gs[i + 1])(id));
        const mid = members.filter((id) => !left.includes(id) && !right.includes(id));
        return [...left, ...chain(mid, (id) => linked(id).filter((p) => mid.includes(p)), rank), ...right];
      });
      const ds = ids.map(desired).filter((d) => d !== null);
      return { ids, d: ds.length ? avg(ds) : null };
    }).sort((a, b) => (a.d ?? Infinity) - (b.d ?? Infinity));
    units.forEach((u) => {
      const W = (u.ids.length - 1) * SP;
      const start = u.d !== null ? u.d - W / 2 : placed.length ? Math.max(...placed.map((p) => p.hi)) + SP : 0;
      const cands = [start, ...placed.flatMap((p) => [p.hi + SP, p.lo - SP - W])].filter((c) => fits(c, c + W));
      cands.sort((a, b) => Math.abs(a - start) - Math.abs(b - start));
      put(u.ids, cands[0]);
    });
  });
  const minX = Math.min(...x.values()), maxX = Math.max(...x.values());
  const local = new Map(comp.map((id) => [id, { x: x.get(id) - minX, y: rowOf.get(id) * ROW }]));
  return { local, w: maxX - minX + CARD_W, h: (rows.length - 1) * ROW + CARD_H };
}

export function layoutLineage(ids, edges, byId, titleOf) {
  const par = new Map(ids.map((i) => [i, i]));
  const find = (i) => (par.get(i) === i ? i : find(par.get(i)));
  edges.forEach((e) => { const [a, b] = ends(e); par.set(find(a), find(b)); });
  const comps = new Map();
  [...ids].sort((a, b) => byId.get(a).title.localeCompare(byId.get(b).title)).forEach((id) => push(comps, find(id), id));
  const pos = new Map(), groups = [];
  let y0 = 0, width = 0;
  [...comps.values()].sort((a, b) => b.length - a.length).forEach((comp) => {
    const set = new Set(comp);
    const { local, w, h } = layoutComp(comp, edges.filter((e) => ends(e).every((id) => set.has(id))));
    local.forEach((p, id) => pos.set(id, { x: p.x, y: p.y + y0 + TITLE_H }));
    groups.push({ title: titleOf(comp, byId), y: y0, w, h: h + TITLE_H });
    width = Math.max(width, w);
    y0 += TITLE_H + h + GROUP_GAP;
  });
  return { pos, groups, width, height: Math.max(0, y0 - GROUP_GAP) };
}
